/**
 * The call, as React sees it. Story V2-091.
 *
 * Fifteen lines of wiring and no logic, which is the point: everything that
 * could be wrong is in `call.js`, where `node --test` reaches it, or in
 * `voice-client-core`, where `cargo test` does.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { openDevices } from "../audio/shell.js";
import { createCall, tracer, voiceTransport } from "./call.js";

/**
 * @param {{ endpoint: string, token: string }} config
 * @param {{ sessionId?: string | null }} options
 */
export function useVoiceCall({ endpoint, token }, { sessionId = null } = {}) {
  const [state, setState] = useState(null);
  const [ended, setEnded] = useState(null);
  const [problem, setProblem] = useState(null);
  const [levels, setLevels] = useState(null);
  const call = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let devices = null;

    (async () => {
      const wasm = await import("../../pkg/voice/voice_client_core.js");
      await wasm.default();
      devices = await openDevices({
        onCaptured: (bytes) => call.current?.captured(bytes),
        onDrained: (bytes) => call.current?.drained(bytes),
        trace: tracer(),
      });
      if (cancelled) {
        await devices.close();
        return;
      }

      const core = new wasm.WasmVoiceSession(
        JSON.stringify({
          session: sessionId,
          aec: devices.aec,
          input: {
            codec: "pcm16",
            sample_rate: devices.sampleRate,
            channels: 1,
            sample_format: "pcm16_le",
          },
        }),
        // From the platform's CSPRNG and not the clock: every client on a
        // tower that just came back would read the clock at the same
        // millisecond, and the jitter would jitter nothing (§19.6).
        //
        // `Uint32Array` and not `Float64Array`: Web Crypto takes integer
        // typed arrays only, and a float one is a `TypeMismatchError` —
        // which is what this was, and it failed before the microphone was
        // ever opened. Thirty-two bits is ample for a seed the core spreads
        // through a xorshift to jitter a backoff; the binding takes an
        // `f64` and reads its bits, so an integer-valued double is a
        // perfectly good one.
        crypto.getRandomValues(new Uint32Array(1))[0],
      );

      setLevels({ input: devices.inputLevel, output: devices.outputLevel });
      call.current = createCall({
        core,
        connect: voiceTransport({ endpoint, token }),
        devices,
        onState: setState,
        onEnded: setEnded,
      });
    })().catch((error) => {
      if (!cancelled) {
        setProblem(error?.message ?? "the call could not be started");
      }
    });

    return () => {
      cancelled = true;
      call.current?.hangUp("page_left");
      call.current = null;
      devices?.close();
    };
    // Strings, not the object they arrived in. A caller writing
    // `useVoiceCall({ endpoint, token: config.token })` builds a fresh
    // object every render, and an effect that depended on it tore the call
    // down and rebuilt it — new `AudioContext`, new core, new socket — on
    // every state update it had just caused. Six sockets in three hundred
    // milliseconds, and a call permanently "connecting".
  }, [endpoint, token, sessionId]);

  const hangUp = useCallback(() => call.current?.hangUp(), []);
  const say = useCallback((text) => call.current?.say(text), []);
  // No local `muted` state: the core publishes it and the screen renders
  // from `state`. A second copy here is a second copy to be wrong (§19.4).
  const setMuted = useCallback((muted) => call.current?.setMuted(muted), []);

  return { state, ended, problem, levels, hangUp, say, setMuted };
}
